import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import { useDataProvider } from 'src/app/common/utils';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import { useIntl } from 'react-intl';
import { ParameterPaginateList, ParameterResultListParam } from '../../../types/parameter-setting';
import { getDayEnd, getDayStart } from 'src/app/common/utils';
import { fetchParameterResultList } from '../../../network/parameterCrud';
import ParameterSettingList from './components/ParameterSettingList';
import { map } from 'lodash';
import { CriteriaTypeEnum } from '../../../types/criteria-setting';

type ParameterListingPageProps = {} & ParamsProps;

const initialState: ParameterResultListParam = {
  name: '',
  code: '',
  type: '',
  updatedBy: '',
  updatedDateFrom: null,
  updatedDateTo: null,
  page: 1,
  limit: 5,
};

export const parameterFilterKeys = ['name', 'code', 'type', 'updatedBy', 'updatedDateFrom', 'updatedDateTo'];

const paramsInitiator = (initialParams?: Record<string, string>): ParameterResultListParam => {
  return initialParams
    ? {
        name: initialParams.name || '',
        type: initialParams.type || '',
        code: initialParams.code || '',
        updatedBy: initialParams.updatedBy || '',
        updatedDateFrom: initialParams.updatedDateFrom ? getDayStart(new Date(initialParams.updatedDateFrom)) : null,
        updatedDateTo: initialParams.updatedDateTo ? getDayEnd(new Date(initialParams.updatedDateTo)) : null,
        page: 1,
        limit: 5,
      }
    : initialState;
};

const ParameterSettingListingPage: FC<ParameterListingPageProps> = ({ initialParams, onChangeQueryParams }) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const [filterState, setFilterState] = useState<ParameterResultListParam>(paramsInitiator(initialParams));
  const [sortKey, setSortKey] = useState<{ key: string; value?: string }[]>([]);
  const [parameterList, setParameterList] = useState<ParameterPaginateList>();

  const updateSortingKey = (sortingKey: { key: string; value?: string }) => {
    const sortingKeyArray = sortKey.filter((currentValue, index, arr) => {
      return currentValue.key !== sortingKey.key;
    });
    sortingKeyArray.unshift(sortingKey);
    setSortKey(sortingKeyArray);
    refreshData();
  };

  const { isLoading, refreshData } = useDataProvider<ParameterPaginateList>(
    async () => {
      try {
        if (onChangeQueryParams) {
          onChangeQueryParams(filterState);
        }
        return await fetchParameterResultList(filterState, sortKey, dispatch);
      } catch (err) {}
    },
    setParameterList,
    false,
  );

  return (
    <>
      <PruFilter
        title={Translation('parameterSetting.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'code',
            initialValue: filterState.code,
            displayName: Translation('parameterSetting.code'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'name',
            initialValue: filterState.name,
            displayName: Translation('parameterSetting.name'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'updatedBy',
            initialValue: filterState.updatedBy,
            displayName: Translation('parameterSetting.lastUpdatedBy'),
            choices: [
              { displayName: '', value: '' },
              ...(parameterList?.filter?.updatedBy
                ?.filter((item) => item?.toString()?.length)
                ?.map?.((item) => {
                  return {
                    displayName: item,
                    value: item,
                  };
                }) || []),
            ],
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'updatedDateFrom',
            fieldTo: 'updatedDateTo',
            initialDateFrom: filterState.updatedDateFrom,
            initialDateTo: filterState.updatedDateTo,
            displayName: Translation('parameterSetting.lastUpdatedAt'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'type',
            initialValue: filterState.type,
            displayName: Translation('parameterSetting.type'),
            choices: [
              { displayName: '', value: '' },
              ...map(CriteriaTypeEnum, (option: string) => ({ displayName: option, value: option.toUpperCase() })),
            ],
          },
        ]}
        onChangeFilter={(data) => {
          setFilterState({
            ...filterState,
            name: data.name,
            code: data.code,
            type: data.type,
            updatedBy: data.updatedBy,
            updatedDateFrom: data.updatedDateFrom,
            updatedDateTo: data.updatedDateTo,
          });
        }}
        fetchData={refreshData}
      />
      <ParameterSettingList
        isLoading={isLoading}
        onRefresh={refreshData}
        parameterList={parameterList}
        onSort={(dataKey) => updateSortingKey(dataKey)}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default ParameterSettingListingPage;
